import { useAppDataPrefetcher } from "@/api/prefetch";
import { ResponsiveShell } from "@/components/layout/responsive-shell";
import { LoadingScreen } from "@/components/loading-screen";
import { ConfirmationProvider } from "@/components/ui/confirmation-dialog";
import { ToastProvider } from "@/components/ui/toast";
import { FeatureTourLayer, TourTargetProvider } from "@/features/feature-tours";
import { OnboardingGate } from "@/features/onboarding";
import { PolicyAcceptanceGate, usePolicyAcceptance } from "@/features/policy-acceptance";
import { AuthProvider, useAuth } from "@/hooks/use-auth";
import { useAuthStatus } from "@/hooks/use-auth-status";
import { useNotificationWebSocket } from "@/hooks/use-notification-websocket";
import { useUnifiedColorScheme } from "@/hooks/use-unified-color-scheme";
import '@/lib/nativewind-interop';
import { QueryProvider } from "@/providers/query-provider";
import { ResponsiveShellProvider } from "@/providers/responsive-shell-provider";
import { UnifiedThemeProvider, useAppTheme } from "@/providers/theme-provider";
import { UnitsProvider } from "@/providers/units-provider";
import Ionicons from "@expo/vector-icons/Ionicons";
import { useFonts } from "expo-font";
import { Stack, usePathname, useRouter } from "expo-router";
import * as SplashScreen from "expo-splash-screen";
import { StatusBar } from "expo-status-bar";
import React, { useEffect, useRef } from "react";
import { GestureHandlerRootView } from "react-native-gesture-handler";
import { SafeAreaProvider } from "react-native-safe-area-context";
import "../../global.css";

import { wrap as sentryWrap } from '@/utils/sentry';

// Prevent the splash screen from auto-hiding before asset loading is complete.
SplashScreen.preventAutoHideAsync();

// Component to handle prefetching when auth becomes verified
function PrefetchOnVerified() {
  const authStatus = useAuthStatus();
  const { user } = useAuth();
  const prefetcher = useAppDataPrefetcher();
  const hasPrefetchedRef = useRef(false);

  useEffect(() => {
    // Prefetch essential data when auth transitions to verified
    if (authStatus === 'verified' && user?.id && !hasPrefetchedRef.current) {
      hasPrefetchedRef.current = true;
      prefetcher.prefetchEssentialData(user.id).catch((error) => {
        console.warn('Prefetch failed:', error);
        // Don't block app initialization on prefetch failures
      });
    }

    // Reset prefetch flag when user logs out
    if (authStatus === 'loggedOut') {
      hasPrefetchedRef.current = false;
    }
  }, [authStatus, user?.id, prefetcher]);

  return null;
}

function VerifiedUserGates() {
  const { status } = usePolicyAcceptance();

  return (
    <>
      <PolicyAcceptanceGate />
      {status === 'clear' && <OnboardingGate />}
      {status === 'clear' && <FeatureTourLayer />}
    </>
  );
}

// Centralized route protection component
function ProtectedRoutes() {
  const authStatus = useAuthStatus();
  const { user } = useAuth();
  const pathname = usePathname();
  const router = useRouter();
  const theme = useAppTheme();
  useNotificationWebSocket();
  const hasResolvedOnceRef = React.useRef(false);

  // Track when auth has resolved at least once (initial boot complete)
  React.useEffect(() => {
    if (authStatus !== 'loading') {
      hasResolvedOnceRef.current = true;
    }
  }, [authStatus]);

  // Handle unverified users - always redirect to verify-otp
  useEffect(() => {
    if (authStatus === 'unverified' && user?.email) {
      const currentPath = pathname || '';
      if (!currentPath.includes('verify-otp')) {
        const email = user.email || '';
        router.replace({
          pathname: '/(auth)/verify-otp',
          params: { email }
        });
      }
    }
  }, [authStatus, user?.email, pathname, router]);

  // Handle verified users who ended up on auth routes -- redirect to app
  useEffect(() => {
    if (authStatus === 'verified') {
      const currentPath = pathname || '';
      if (currentPath.includes('/(auth)')) {
        router.replace({ pathname: '/(tabs)/discover' });
      }
    }
  }, [authStatus, pathname, router]);

  // Handle logged-out users trying to access protected routes - redirect to login
  // IMPORTANT: Never redirect if already on an auth route (preserves register/login state)
  useEffect(() => {
    if (authStatus === 'loggedOut') {
      const currentPath = pathname || '';
      const isOnAuthRoute = currentPath.includes('/(auth)');

      if (!isOnAuthRoute && (currentPath.startsWith('/(tabs)') || currentPath.startsWith('/(root)'))) {
        router.replace({ pathname: '/(auth)/login' });
      }
    }
  }, [authStatus, pathname, router]);

  // Show loading screen ONLY on initial boot (before auth has resolved once)
  // After initial boot, preserve navigation state even during loading transitions
  const isInitialLoading = authStatus === 'loading' && !hasResolvedOnceRef.current;

  // On initial boot, show loading screen and don't render Stack yet
  if (isInitialLoading) {
    return <LoadingScreen message="Checking authentication..." />;
  }

  // After initial boot, always render Stack to preserve navigation state
  // Even if authStatus becomes 'loading' again (e.g., on tab refocus), keep Stack mounted
  return (
    <>
      {/* Trigger prefetch when auth becomes verified */}
      {authStatus === 'verified' && <PrefetchOnVerified />}
      {/* Gate on policy acceptance, then onboarding -- policy must clear first */}
      {authStatus === 'verified' && <VerifiedUserGates />}

      {/* ResponsiveShellProvider manages drawer state and responsive breakpoints */}
      <ResponsiveShellProvider>
        {/* ResponsiveShell renders sidebar (desktop) or drawer (mobile) for authenticated screens */}
        <ResponsiveShell>
          {/* Always render Stack to preserve navigation state */}
          <Stack
            screenOptions={{
              headerShown: false,
              contentStyle: { backgroundColor: theme.colors.background }
            }}
          >
            {/* Auth screens: available when logged out, unverified, or loading (after initial boot)
                This prevents route remounting when authStatus transitions between loading/loggedOut */}
            <Stack.Protected guard={
              authStatus === 'loggedOut' ||
              authStatus === 'unverified' ||
              (authStatus === 'loading' && hasResolvedOnceRef.current)
            }>
              <Stack.Screen name="(auth)" options={{ headerShown: false }} />
            </Stack.Protected>

            {/* Authenticated screens: available only when verified */}
            <Stack.Protected guard={
              authStatus === 'verified' ||
              (authStatus === 'loading' && hasResolvedOnceRef.current)
            }>
              <Stack.Screen name="(tabs)" options={{ headerShown: false }} />
              <Stack.Screen name="(root)" options={{ headerShown: false }} />
            </Stack.Protected>
          </Stack>
        </ResponsiveShell>
      </ResponsiveShellProvider>
    </>
  );
}

// Inner component that can access the theme
function AppContent() {
  const theme = useAppTheme();
  const colorScheme = useUnifiedColorScheme();

  return (
    <GestureHandlerRootView style={{ flex: 1 }}>
      <QueryProvider>
        <AuthProvider>
          <TourTargetProvider>
            <ToastProvider>
              <ConfirmationProvider>
                <SafeAreaProvider>
                  <ProtectedRoutes />
                </SafeAreaProvider>
              </ConfirmationProvider>
            </ToastProvider>
          </TourTargetProvider>
        </AuthProvider>
      </QueryProvider>
      <StatusBar style={colorScheme === 'dark' ? 'light' : 'dark'} />
    </GestureHandlerRootView>
  );
}

// Main root layout component
function RootLayout() {
  const [loaded] = useFonts({
    "Franie-Regular": require("@/assets/fonts/Franie-Regular.otf"),
    "SpaceMono-Regular": require("@/assets/fonts/SpaceMono-Regular.ttf"),
    ...Ionicons.font,
  });

  useEffect(() => {
    if (loaded) {
      SplashScreen.hideAsync();
    }
  }, [loaded]);

  if (!loaded) {
    return null;
  }

  return (
    <UnifiedThemeProvider>
      <UnitsProvider>
          <AppContent />
      </UnitsProvider>
    </UnifiedThemeProvider>
  );
}

export default sentryWrap(RootLayout);
